import React from "react";
import { Wrench, Cpu, Settings, ClipboardList, Users } from "lucide-react";
import Seo from "@/components/Seo";
import PhotoBackdrop from "@/components/PhotoBackdrop";
import Breadcrumb from "@/components/Breadcrumb";
import CTABanner from "@/components/CTABanner";
import AboutRelated from "@/components/AboutRelated";

// Deliberately compliance-safe copy:
// - No named individuals, photos, personal qualifications or DBS claims.
// - No headcount specifics. "Team introductions on contract award."
// - Disciplines listed match the services we already publish.

const DISCIPLINES = [
  {
    icon: Cpu,
    title: "Controls & Automation",
    body: "PLC programming, HMI/SCADA and industrial automation — from spec through commissioning.",
  },
  {
    icon: Wrench,
    title: "Mechanical Engineering",
    body: "Hydraulics, pneumatics, water systems, welding, fabrication and machine installation.",
  },
  {
    icon: Settings,
    title: "Electrical Engineering",
    body: "Industrial installation, control panel design & build, fire alarms and emergency lighting.",
  },
  {
    icon: ClipboardList,
    title: "Compliance & Testing",
    body: "EICR/fixed-wire testing, PAT testing, thermal imaging and planned preventive maintenance.",
  },
  {
    icon: Users,
    title: "Operations & Coordination",
    body: "Ops, planning and reporting keep the Operations Portal up to date so clients see progress in real time.",
  },
];

const TeamPage = () => (
  <div data-testid="about-team-page">
    <Seo
      title="The Team"
      description="Lutrom Engineering is a multi-disciplinary team of mechanical, electrical, controls and compliance engineers based at our Northwich workshop. Named site-lead introductions on contract award."
      path="/about/team"
    />

    <section className="relative overflow-hidden">
      <PhotoBackdrop src="/gallery/workshop.jpg" position="center 45%" />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-14 lg:pb-20 relative">
        <p className="eyebrow">About · The Team</p>
        <h1
          className="font-display mt-6 text-white"
          style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 0.98 }}
          data-testid="team-heading"
        >
          THE ENGINEERS
          <br />
          <span className="gradient-blue-text">BEHIND THE WORK.</span>
        </h1>
        <p className="mt-6 max-w-3xl text-[16px] leading-relaxed text-[color:var(--lt-text-dim)]">
          Lutrom is a multi-disciplinary engineering team based at Unit 28,
          Winnington Business Park in Northwich. We work across mechanical,
          electrical, controls and compliance — so a single phone call reaches
          people who can actually help, whatever the site or symptom.
        </p>
      </div>
    </section>

    <Breadcrumb items={[{ label: "About", to: "/about" }, { label: "The Team" }]} />

    <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-16">
      <p className="eyebrow">Disciplines under one roof</p>
      <h2
        className="font-display mt-4 text-white"
        style={{ fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1 }}
      >
        FIVE DISCIPLINES, ONE PHONE NUMBER.
      </h2>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {DISCIPLINES.map((d) => {
          const Icon = d.icon;
          return (
            <div
              key={d.title}
              className="service-card rounded-xl p-6"
              data-testid={`team-discipline-${d.title.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
            >
              <span className="icon-tile" style={{ width: 44, height: 44 }}>
                <Icon size={20} />
              </span>
              <h3
                className="font-display mt-5 text-white"
                style={{ fontSize: 19, letterSpacing: "0.02em" }}
              >
                {d.title.toUpperCase()}
              </h3>
              <p className="mt-3 text-[13.5px] text-[color:var(--lt-text-dim)] leading-relaxed">
                {d.body}
              </p>
            </div>
          );
        })}
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <p className="eyebrow">How we work</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(28px, 3.4vw, 44px)", lineHeight: 1 }}
        >
          SMALL TEAMS.
          <br />
          <span className="gradient-blue-text">LONG RELATIONSHIPS.</span>
        </h2>
      </div>
      <div className="lg:col-span-7 space-y-5 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
        <p>
          Every account has a named site lead once a contract is awarded. You
          get the mobile number of the engineer who knows your kit — not a
          generic ticket queue. Behind them sits the wider team, an ops
          coordinator, and the workshop.
        </p>
        <p>
          We deliberately do not publish individual staff details, photos or
          personal qualifications on the public site. Named introductions,
          engineer competency evidence, insurance, DBS status (where relevant)
          and RAMS templates are all available to prospective clients on
          request.
        </p>
        <p>
          Site attendance is by qualified engineers only — never
          sub-contracted labour we haven&apos;t met. City &amp; Guilds electrical
          qualifications sit behind our electrical trades; controls, mechanical
          and safety training records are held on file and shared under NDA.
        </p>
      </div>
    </section>

    <CTABanner
      variant="standard"
      title="Want to meet the team who&#39;d look after your site?"
      body="Tell us what you run and where. We&#39;ll put you in touch with the engineer who&#39;d own the account."
      phoneLocation="about_team_cta"
    />

    <AboutRelated excludeSlug="team" />
  </div>
);

export default TeamPage;
